import { useState } from "react";
import { Link2, Check } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { useStore } from "@/context/StoreContext";
import { WhatsAppIcon, XIcon, PinterestIcon, FacebookIcon } from "@/components/SocialIcons";

const btn =
  "flex h-10 w-10 items-center justify-center border border-bone/15 text-bone-dim transition-colors duration-300 hover:border-brass/60 hover:text-brass";

export default function ShareButtons({ art, title }) {
  const { t } = useLang();
  const { settings } = useStore();
  const [copied, setCopied] = useState(false);
  const origin = settings.site_url || window.location.origin;
  const shareUrl = `${origin}/api/share/${art.artwork_id}`;
  const text = `${title} — DEMOIVRE`;
  const enc = encodeURIComponent;
  const image = art.image_url ? (art.image_url.startsWith("http") ? art.image_url : `${origin}${art.image_url}`) : "";

  const links = [
    { id: "whatsapp", label: "WhatsApp", href: `https://wa.me/?text=${enc(`${text} ${shareUrl}`)}`, Icon: WhatsAppIcon },
    { id: "x", label: "X", href: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(shareUrl)}`, Icon: XIcon },
    { id: "pinterest", label: "Pinterest", href: `https://pinterest.com/pin/create/button/?url=${enc(shareUrl)}&media=${enc(image)}&description=${enc(text)}`, Icon: PinterestIcon },
    { id: "facebook", label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`, Icon: FacebookIcon },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success(t("share.copied"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("share.copyErr"));
    }
  };

  return (
    <div data-testid="share-buttons" className="mt-8 flex flex-wrap items-center gap-3">
      <span className="mr-2 font-mono text-[9px] tracking-[0.3em] text-bone-muted">{t("share.label")}</span>
      {links.map(({ id, label, href, Icon }) => (
        <a
          key={id}
          data-testid={`share-${id}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          title={label}
          className={btn}
        >
          <Icon size={15} />
        </a>
      ))}
      <button data-testid="share-copy" type="button" onClick={copy} aria-label={t("share.copy")} title={t("share.copy")} className={btn}>
        {copied ? <Check size={15} /> : <Link2 size={15} />}
      </button>
    </div>
  );
}
